import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useDolfinClient } from "@/components/DolfinProvider";
import { apiErrorMessage } from "@/lib/api-error";
import { BillReviewerMultiSelect } from "@/components/BillReviewerMultiSelect";
import { CurrencyCombobox } from "@/components/CurrencyCombobox";
import { SupplierMultiSelect } from "@/components/SupplierMultiSelect";
import {
  ACTION_OPTIONS,
  STANDING_OPTIONS,
  type ApprovalPolicyAction,
  type ApprovalPolicyResponse,
  type SupplierStanding,
  conditionSummary,
} from "@/lib/approval-policy-utils";
import { Eyebrow } from "@/components/PageUi";
import { cn } from "@/lib/utils";
import type { components } from "@dolfin/api-admin";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Spinner } from "@/components/ui/spinner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

type UpsertApprovalPolicyRequest = components["schemas"]["UpsertApprovalPolicyRequest"];

const MAX_APPROVERS = 5;
const MAX_PRIORITY = 1000;

export interface ApprovalPolicyFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** When set, the dialog edits this policy (PUT); otherwise it creates one (POST). */
  policy?: ApprovalPolicyResponse | null;
  /** Default priority for a new policy (e.g. one past the current max). */
  defaultPriority?: number;
  onSaved: () => void;
}

function numOrNull(value: string): number | null {
  const trimmed = value.trim();
  if (trimmed === "") return null;
  const n = Number(trimmed);
  return isNaN(n) ? null : n;
}

function ruleSentence({
  amountGte,
  amountLte,
  currency,
  standing,
  supplierCount,
  action,
  approverCount,
}: {
  amountGte: string;
  amountLte: string;
  currency: string;
  standing: SupplierStanding;
  supplierCount: number;
  action: ApprovalPolicyAction;
  approverCount: number;
}) {
  const who =
    supplierCount === 0
      ? "any supplier"
      : supplierCount === 1
        ? "the chosen supplier"
        : `one of ${supplierCount} chosen suppliers`;
  const conditions = conditionSummary({
    amountGte: amountGte || null,
    amountLte: amountLte || null,
    currency: currency || null,
    supplierStanding: standing,
  });
  const outcome =
    action === "AutoApprove"
      ? "it is approved straight away"
      : action === "AutoVoid"
        ? "it is voided"
        : approverCount === 0
          ? "it waits for any admin to review it"
          : `it goes to ${approverCount === 1 ? "the chosen approver" : `${approverCount} approvers`} for review`;
  const scope = conditions === "Any amount" ? "any amount" : conditions;
  return `When a bill from ${who} is submitted (${scope}), ${outcome}.`;
}

export function ApprovalPolicyFormDialog({
  open,
  onOpenChange,
  policy,
  defaultPriority = 100,
  onSaved,
}: ApprovalPolicyFormDialogProps) {
  const api = useDolfinClient();
  const isEdit = !!policy;

  const [name, setName] = useState("");
  const [priority, setPriority] = useState(String(defaultPriority));
  const [active, setActive] = useState(true);
  const [amountGte, setAmountGte] = useState("");
  const [amountLte, setAmountLte] = useState("");
  const [currency, setCurrency] = useState("");
  const [supplierIds, setSupplierIds] = useState<string[]>([]);
  const [standing, setStanding] = useState<SupplierStanding>("Any");
  const [action, setAction] = useState<ApprovalPolicyAction>("AlwaysReview");
  const [approverUserIds, setApproverUserIds] = useState<string[]>([]);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setSubmitting(false);
    if (policy) {
      setName(policy.name);
      setPriority(String(policy.priorityOrder));
      setActive(policy.active);
      setAmountGte(policy.amountGte != null ? String(policy.amountGte) : "");
      setAmountLte(policy.amountLte != null ? String(policy.amountLte) : "");
      setCurrency(policy.currency ?? "");
      setSupplierIds(policy.supplierIds ?? []);
      setStanding(policy.supplierStanding);
      setAction(policy.action);
      setApproverUserIds(policy.approverUserIds ?? []);
    } else {
      setName("");
      setPriority(String(defaultPriority));
      setActive(true);
      setAmountGte("");
      setAmountLte("");
      setCurrency("");
      setSupplierIds([]);
      setStanding("Any");
      setAction("AlwaysReview");
      setApproverUserIds([]);
    }
  }, [open, policy, defaultPriority]);

  const isReview = action === "AlwaysReview";

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    if (!name.trim()) {
      setError("A policy name is required.");
      return;
    }
    const priorityNum = Number(priority);
    if (isNaN(priorityNum) || priorityNum < 0 || priorityNum > MAX_PRIORITY) {
      setError(`Priority order must be between 0 and ${MAX_PRIORITY}.`);
      return;
    }
    const gte = numOrNull(amountGte);
    const lte = numOrNull(amountLte);
    const hasAmount = gte != null || lte != null;
    if ((gte != null && gte < 0) || (lte != null && lte < 0)) {
      setError("Amount bounds must not be negative.");
      return;
    }
    if (hasAmount && !currency.trim()) {
      setError("A currency is required when an amount condition is set.");
      return;
    }
    if (gte != null && lte != null && gte > lte) {
      setError("The lower amount bound must not exceed the upper bound.");
      return;
    }
    if (approverUserIds.length > MAX_APPROVERS) {
      setError(`At most ${MAX_APPROVERS} approvers are allowed.`);
      return;
    }

    const body: UpsertApprovalPolicyRequest = {
      name: name.trim(),
      priorityOrder: priorityNum,
      active,
      amountGte: gte,
      amountLte: lte,
      currency: hasAmount ? currency.trim().toUpperCase() : null,
      supplierIds,
      supplierStanding: standing,
      action,
      // Approvers only carry meaning for AlwaysReview.
      approverUserIds: isReview ? approverUserIds : [],
    };

    setSubmitting(true);
    const { error: apiError } = isEdit
      ? await api.PUT("/v1/ap/approval-policies/{id}", {
          params: { path: { id: policy!.id } },
          body,
        })
      : await api.POST("/v1/ap/approval-policies", { body });

    if (apiError) {
      const message = apiErrorMessage(apiError, "Failed to save approval policy.");
      setError(message);
      toast.error(message);
      setSubmitting(false);
      return;
    }

    setSubmitting(false);
    toast.success(isEdit ? "Approval policy updated." : "Approval policy created.");
    onOpenChange(false);
    onSaved();
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>{isEdit ? "Edit approval policy" : "New approval policy"}</DialogTitle>
            <DialogDescription>
              Rules are evaluated when a bill is submitted for review. The highest-priority
              matching rule (lower priority number wins) decides the outcome.
            </DialogDescription>
          </DialogHeader>

          <div className="mt-4 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="policy-name">Name</Label>
              <Input
                id="policy-name"
                placeholder="e.g. High value bills"
                value={name}
                maxLength={120}
                onChange={(e) => setName(e.target.value)}
                autoFocus
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="policy-priority">Priority order</Label>
                <Input
                  id="policy-priority"
                  type="number"
                  min={0}
                  max={MAX_PRIORITY}
                  value={priority}
                  onChange={(e) => setPriority(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">0 to {MAX_PRIORITY}. Lower numbers are checked first.</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="policy-active">Active</Label>
                <div className="flex h-9 items-center gap-2">
                  <Switch id="policy-active" checked={active} onCheckedChange={setActive} />
                  <span className="text-sm text-muted-foreground">
                    {active ? "Enabled" : "Disabled"}
                  </span>
                </div>
              </div>
            </div>

            <div className="rounded-lg border bg-muted/30 p-3 space-y-3">
              <Eyebrow>Conditions</Eyebrow>
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="policy-amount-gte" className="text-xs">
                    At least
                  </Label>
                  <Input
                    id="policy-amount-gte"
                    type="number"
                    min={0}
                    step="0.01"
                    placeholder="Any"
                    value={amountGte}
                    onChange={(e) => setAmountGte(e.target.value)}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="policy-amount-lte" className="text-xs">
                    At most
                  </Label>
                  <Input
                    id="policy-amount-lte"
                    type="number"
                    min={0}
                    step="0.01"
                    placeholder="Any"
                    value={amountLte}
                    onChange={(e) => setAmountLte(e.target.value)}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="policy-currency" className="text-xs">
                    Currency
                  </Label>
                  <CurrencyCombobox
                    id="policy-currency"
                    value={currency}
                    onChange={setCurrency}
                    disabled={submitting}
                  />
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                Currency is required when an amount bound is set, and scopes the rule to that currency.
              </p>

              <div className="space-y-1.5">
                <Label className="text-xs">Suppliers</Label>
                <SupplierMultiSelect
                  value={supplierIds}
                  onChange={setSupplierIds}
                  disabled={submitting}
                />
                <p className="text-xs text-muted-foreground">
                  Leave empty to apply to all suppliers.
                </p>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="policy-standing" className="text-xs">
                  Supplier standing
                </Label>
                <Select value={standing} onValueChange={(v) => setStanding(v as SupplierStanding)}>
                  <SelectTrigger id="policy-standing" className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STANDING_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value}>
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Radios, not a dropdown: a dropdown hid two of the three descriptions. */}
            <div className="space-y-2">
              <Label id="policy-action-label">Action</Label>
              <div role="radiogroup" aria-labelledby="policy-action-label" className="grid gap-2 sm:grid-cols-3">
                {ACTION_OPTIONS.map((o) => {
                  const selected = action === o.value;
                  return (
                    <button
                      key={o.value}
                      type="button"
                      role="radio"
                      aria-checked={selected}
                      onClick={() => setAction(o.value)}
                      className={cn(
                        "rounded-lg border p-3 text-left transition-colors hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50",
                        selected && "border-accent-blue/50 bg-accent-blue/5 hover:bg-accent-blue/5",
                      )}
                    >
                      <span className="flex items-center gap-2 text-sm font-medium">
                        <span
                          aria-hidden
                          className={cn(
                            "grid size-3.5 place-items-center rounded-full border",
                            selected && "border-accent-blue",
                          )}
                        >
                          {selected ? <span className="size-1.5 rounded-full bg-accent-blue" /> : null}
                        </span>
                        {o.label}
                      </span>
                      <span className="mt-1 block text-xs text-muted-foreground">{o.description}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Approvers</Label>
              <BillReviewerMultiSelect
                value={approverUserIds}
                onChange={setApproverUserIds}
                disabled={submitting || !isReview}
              />
              <p className="text-xs text-muted-foreground">
                {isReview
                  ? `Who the bill is routed to. Max ${MAX_APPROVERS}. Leave empty so any admin can action it.`
                  : "Approvers only apply when the action is Review."}
              </p>
            </div>

            <div className="rounded-lg border border-dashed px-3 py-2.5 text-sm">
              <Eyebrow className="mb-1">In words</Eyebrow>
              {ruleSentence({
                amountGte,
                amountLte,
                currency,
                standing,
                supplierCount: supplierIds.length,
                action,
                approverCount: approverUserIds.length,
              })}
            </div>

            {error && <p className="text-destructive text-sm">{error}</p>}
          </div>

          <DialogFooter className="mt-6">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting && <Spinner className="size-4" />}
              {isEdit ? "Save changes" : "Create policy"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
